import React from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import moment from "moment";
import AttachmentModal from "./attachmentModal";
import { useAuth } from "../../Context/authContext";
import { statuses } from "../helpers";
import "../../Styles/tables.css";

const ProjectTable = ({
  projects,
  sortBy,
  setSortBy,
  sortOrderActivity,
  setSortOrderActivity,
  sortOrderStart,
  setSortOrderStart,
  sortOrderEnd,
  setSortOrderEnd,
}) => {
  const { user } = useAuth();
  const columnTemplate = "10% 22% 12% 8% 8% 18% 10% 12%";

  const handleSort = (column) => {
    setSortBy(column);
    if (column === "updatedAt") {
      setSortOrderActivity((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else if (column === "start_date") {
      setSortOrderStart((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else if (column === "end_date") {
      setSortOrderEnd((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    }
  };

  const renderHeader = () => (
    <Box
      className="table-head"
      display="grid"
      gridTemplateColumns={columnTemplate}
      px={4}
      pl={{ xs: 2, md: 4 }}
      py={1}
      sx={{
        backgroundColor: (t) => t.palette.primary.main, //"#041e41",
        color: (t) => t.palette.text.main,
        fontWeight: "bold",
      }}
    >
      {[
        {
          label: "Last Activity",
          key: "updatedAt",
          order: sortOrderActivity,
        },
        { label: "Project Name" },
        { label: "Project Manager" },
        { label: "Start Date", key: "start_date", order: sortOrderStart },
        { label: "End Date", key: "end_date", order: sortOrderEnd },
        { label: "Unit" },
        { label: "Status" },
        { label: "Actions" },
      ].map((col, idx) => (
        <Box
          key={idx}
          display="flex"
          alignItems="center"
          gap={1}
          pr={2}
          flexWrap={"wrap"}
        >
          <Typography variant="body1" fontWeight="bold">
            {col.label}
          </Typography>
          {col.key && (
            <Button
              onClick={() => handleSort(col.key)}
              size="small"
              sx={{
                minWidth: "24px",
                padding: 0,
                color: (t) => t.palette.text.main,
              }}
            >
              {col.order === "ASC" ? (
                <ArrowUpwardIcon fontSize="small" />
              ) : (
                <ArrowDownwardIcon fontSize="small" />
              )}
            </Button>
          )}
        </Box>
      ))}
    </Box>
  );

  const renderRow = (project) => {
    const isAccountant = user.userData.userRoles.includes("Accounting");
    const isManager = user.userData.userRoles.includes("Unit Manager");
    const isUnitManager =
      isManager &&
      (user.userData.managedUnits.includes(project.unit_id) ||
        project.approvers?.find(
          (a) => a.approver_email === user.userData.userName,
        ));
    const isAdmin = user.userData.userRoles.includes("Administrator");
    const canEdit =
      (user.userData.userName === project.project_manager_email &&
        [statuses.DRAFT, statuses.PREPARING_FOR_SIGNOFF].includes(
          project.status_id,
        )) ||
      isAdmin ||
      isAccountant ||
      isUnitManager;
    return (
      <Box
        key={project.project_id}
        display="grid"
        gridTemplateColumns={columnTemplate}
        px={4}
        pl={{ xs: 2, md: 4 }}
        py={1}
        sx={{
          alignItems: "flex-start",
          wordBreak: "break-word",
          borderBottom: "1px solid #ddd",
        }}
      >
        <Typography variant="body1" pr={2}>
          {moment(project.updatedAt).format("DD.MM.YYYY HH:mm:ss") || "error"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {project.acronym
            ? `${project.acronym}: ${project.name}`
            : project.name || "-"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {project.project_manager_name || "-"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {project.start_date
            ? moment(project.start_date).format("DD.MM.YYYY")
            : "-"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {project.end_date
            ? moment(project.end_date).format("DD.MM.YYYY")
            : "-"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {project.Unit?.unit_name || "-"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {project.Status.status_name || "error"}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            href={`/project/${project.uid}`}
            size="small"
          >
            View
          </Button>
          {canEdit && (
            <Button
              variant="contained"
              href={`/project/edit/${project?.uid}`}
              size="small"
            >
              Edit
            </Button>
          )}
          {(project.attachments?.length ?? 0) !== 0 && (
            <AttachmentModal
              attachments={project.attachments}
              uid={project.uid}
            ></AttachmentModal>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ overflowX: "auto", width: "100%" }}>
      <Box sx={{ minWidth: "950px" }}>
        {renderHeader()}
        {(projects?.length ?? 0) > 0 ? (
          projects.map(renderRow)
        ) : (
          <Typography variant="subtitle2" padding={2}>
            No Projects
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ProjectTable;
